import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  ActiveDocument,
  CreateDocumentsPayload,
  createRodoPolicyDocument,
  getActiveRodoPolicy,
  getRodoPoliciesDocuments,
  RodoDocumentsResponse,
} from '@app/api/rodo-policies.api'

import { RodoDocumentsStatus, Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'

export type RodoDocumentsState = {
  items: RodoDocumentsResponse[]
  activeDocument: ActiveDocument
  status: Status
  createStatus: Status
  activeDocumentStatus: Status
  error: string | null
}

const initialState: RodoDocumentsState = {
  items: [],
  activeDocument: null,
  status: 'idle',
  createStatus: 'idle',
  activeDocumentStatus: 'idle',
  error: null,
}

export const getRodoPoliciesDocumentsData = createAsyncThunk<RodoDocumentsResponse[]>(
  'rodoDocuments/all',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getRodoPoliciesDocuments()

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getActiveRodoPolicyData = createAsyncThunk<ActiveDocument>(
  'rodoDocuments/active',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getActiveRodoPolicy()
      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createRodoPolicyDocumentData = createAsyncThunk<string, CreateDocumentsPayload>(
  'rodoDocuments/create',
  async (createRodoPolicyDocumentPayload, { rejectWithValue }) => {
    try {
      const id = await createRodoPolicyDocument(createRodoPolicyDocumentPayload)

      return id
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

const rodoDocumentsSlice = createSlice({
  name: 'rodoDocuments',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getRodoPoliciesDocumentsData.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(createRodoPolicyDocumentData.pending, (state) => {
        state.createStatus = 'loading'
      })
      .addCase(getActiveRodoPolicyData.pending, (state) => {
        state.activeDocumentStatus = 'loading'
      })
      .addCase(getRodoPoliciesDocumentsData.rejected, (state) => {
        state.status = 'failed'
      })
      .addCase(createRodoPolicyDocumentData.rejected, (state) => {
        state.createStatus = 'failed'
      })
      .addCase(getActiveRodoPolicyData.rejected, (state) => {
        state.activeDocumentStatus = 'failed'
      })
      .addCase(getRodoPoliciesDocumentsData.fulfilled, (state, action) => {
        state.items = action.payload
        state.status = 'succeeded'
      })
      .addCase(getActiveRodoPolicyData.fulfilled, (state, action) => {
        state.activeDocument = action.payload
        state.activeDocumentStatus = 'succeeded'
      })
      .addCase(createRodoPolicyDocumentData.fulfilled, (state, action) => {
        const id = action.payload
        const requestData = action.meta.arg
        const activeIndex = state.items.findIndex((it) => it.status === 'ACTIVE')
        const updatedItems = [...state.items]

        if (activeIndex !== -1) {
          const active = updatedItems[activeIndex]
          updatedItems[activeIndex] = { ...active, status: 'ARCHIVED' as RodoDocumentsStatus }
        }

        const newItem = {
          id,
          status: 'ACTIVE' as RodoDocumentsStatus,
          createdAt: new Date().toISOString(),
          ...requestData,
        }

        state.items = [newItem, ...updatedItems]

        state.createStatus = 'succeeded'
      })
  },
})

export default rodoDocumentsSlice.reducer
